import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { WarningOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import FlowDialog from '../../../../components/ui/FlowDialog';
import FlowOutcome from '../../../../components/ui/FlowOutcome';
import { fadeSwap } from '../../../rooms/rooms.motion';
import { useTourMotion } from '../../../welcome-tour/motion';
import { useSimulateActions, useSimulateResult, useSimulateState } from '../SimulateTradeContext';
import { SIMULATE_DIALOG_WIDTH_PX, STATUS_PROGRESS } from '../constants';
import { SIMULATE_COPY } from '../content';
import { simulateDialogSx } from '../styles';
import type { SimulateStatus } from '../useSimulateTrade';
import { MasterOrderForm } from './MasterOrderForm';
import { SimulateActionBar } from './SimulateActionBar';
import { SimulateResultPanel } from './SimulateResultPanel';
import { EditOrderButton, RerunButton, SimulateResultActions } from './SimulateResultActions';
import { PersistMappingPrompt } from './PersistMappingPrompt';
import { SimulateRoute } from './SimulateRoute';
import { SimulateTradeIcon } from './SimulateTradeIcon';

const { dialog, result, failure, actions } = SIMULATE_COPY;

type View = 'configure' | 'result' | 'failed';
// No entry for 'submitting': a request in flight keeps the current view (re-runs keep the result on screen).
const STATUS_VIEW: Partial<Record<SimulateStatus, View>> = { idle: 'configure', success: 'result', error: 'failed' };

export const SimulateTradeDialog = () => {
  const { v } = useTourMotion();
  const { isOpen, formKey } = useSimulateState();
  const { status, loading, error } = useSimulateResult();
  const { close, resetForm } = useSimulateActions();
  const [view, setView] = useState<View>('configure');

  useEffect(() => {
    const next = STATUS_VIEW[status];
    if (next) setView(next);
  }, [status]);

  const handleClose = () => {
    close();
    resetForm();
  };

  const renderView = () => {
    switch (view) {
      case 'result':
        return (
          <Stack spacing={2.5}>
            <SimulateResultPanel />
            <PersistMappingPrompt />
          </Stack>
        );
      case 'failed':
        return (
          <FlowOutcome
            icon={<WarningOutlined />}
            title={failure.title}
            body={error || failure.fallback}
            actions={
              <>
                <EditOrderButton />
                <RerunButton label={actions.retry} />
              </>
            }
          />
        );
      default:
        return (
          <Stack spacing={3}>
            <Stack spacing={1}>
              <SimulateRoute />
              <Typography variant="caption" color="text.secondary">
                {dialog.intro}
              </Typography>
            </Stack>
            <MasterOrderForm key={formKey} />
          </Stack>
        );
    }
  };

  return (
    <FlowDialog
      open={isOpen}
      onClose={handleClose}
      icon={<SimulateTradeIcon />}
      eyebrow={dialog.eyebrow}
      title={view === 'configure' ? dialog.title : result.title}
      titleId="simulate-trade-dialog-title"
      closeLabel={dialog.close}
      maxWidth={SIMULATE_DIALOG_WIDTH_PX}
      progress={STATUS_PROGRESS[status]}
      busy={loading}
      footer={view === 'configure' ? <SimulateActionBar /> : view === 'result' ? <SimulateResultActions /> : undefined}
      sx={simulateDialogSx}
    >
      <AnimatePresence exitBeforeEnter initial={false}>
        <Box key={view} component={motion.div} variants={v(fadeSwap)} initial="hidden" animate="show" exit="exit">
          {renderView()}
        </Box>
      </AnimatePresence>
    </FlowDialog>
  );
};
